/**
 * The filter panel's state, the Mac side of the phone's `PeopleFilter` (apps/ios/Hub/Model/Filter.swift). Every row
 * writes one clause of the search grammar, in the same order and spelling as the phone, so a smart group saved on
 * either device reads the same on the other. The panel and the typed query compose with AND.
 */
import type { ContactSort, SearchNode } from "@hub/shared";

export type Tri = "any" | "has" | "missing";
export type When = "any" | "30d" | "90d" | "1y" | "thismonth" | "never";
export type Added = "any" | "7d" | "30d" | "1y";

export interface PeopleFilter {
  groupIds: string[];
  tiers: number[];
  sources: string[];
  lastMet: When;
  lastEmail: When;
  lastText: When;
  has: Record<string, Tri>;
  location: string;
  company: string;
  title: string;
  interest: string;
  onHub: boolean;
  added: Added;
  archived: boolean;
  isPrivate: boolean;
  memorialized: boolean;
  custom: Record<string, string>;
  sort: ContactSort;
}

export function emptyFilter(sort: ContactSort = "name"): PeopleFilter {
  return { groupIds: [], tiers: [], sources: [], lastMet: "any", lastEmail: "any", lastText: "any", has: {}, location: "", company: "", title: "", interest: "", onHub: false, added: "any", archived: false, isPrivate: false, memorialized: false, custom: {}, sort };
}

/** The Mac People sort menu; the phone's PeopleSort one to one. */
export const SORTS: { id: ContactSort; label: string }[] = [
  { id: "name", label: "Name" },
  { id: "last_interaction", label: "Last interaction" },
  { id: "score", label: "Relationship strength" },
  { id: "interaction_count", label: "Interaction count" },
  { id: "next_event", label: "Next meeting" },
  { id: "created", label: "Date added" },
  { id: "updated", label: "Recently updated" },
];

export const WHEN_OPTIONS: { id: When; label: string }[] = [
  { id: "any", label: "Any time" },
  { id: "30d", label: "Over 30 days ago" },
  { id: "90d", label: "Over 90 days ago" },
  { id: "1y", label: "Over a year ago" },
  { id: "thismonth", label: "This month" },
  { id: "never", label: "Never" },
];

export const ADDED_OPTIONS: { id: Added; label: string }[] = [
  { id: "any", label: "Any time" },
  { id: "7d", label: "This week" },
  { id: "30d", label: "This month" },
  { id: "1y", label: "This year" },
];

/** Grammar target and row label, in the phone's order. */
export const HAS_TARGETS: [string, string][] = [
  ["need", "Need"],
  ["service", "Offer"],
  ["phone", "Phone"],
  ["email", "Email"],
  ["birthday", "Birthday"],
  ["avatar", "Photo"],
  ["notes", "Notes"],
  ["location", "Location"],
  ["company", "Company"],
];

/** Grammar tier number, row label and the colour token (none for the two lowest). */
export const TIERS: { tier: number; label: string; color: string | null }[] = [
  { tier: 4, label: "Closest", color: "var(--tier-closest)" },
  { tier: 3, label: "Closer", color: "var(--tier-closer)" },
  { tier: 2, label: "Close", color: "var(--tier-close)" },
  { tier: 1, label: "Acquaintance", color: null },
  { tier: 0, label: "None", color: null },
];

const TIER_NAMES: Record<number, string> = { 0: "none", 1: "acquaintance", 2: "close", 3: "closer", 4: "closest" };

const SOURCE_NAMES: Record<string, string> = {
  apple_contacts: "Apple Contacts",
  manual: "Added by hand",
  google: "Google",
  microsoft: "Microsoft",
  linkedin: "LinkedIn",
  csv: "CSV import",
  hub: "Hub",
  extension: "Browser extension",
  apple_mail: "Mail",
  apple_messages: "Messages",
  apple_calendar: "Calendar",
  apple_calls: "Phone",
  ios_contacts: "Phone Contacts",
  ios_calendar: "Phone Calendar",
  email: "Email",
};

export function sourceName(source: string): string {
  return SOURCE_NAMES[source] ?? source.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Quotes a value the way the phone does: only when it holds a space. */
export function quote(value: string): string {
  return value.includes(" ") ? `"${value}"` : value;
}

/** Which row a clause came from, so its chip's x can clear that row alone. */
export type FilterPartKey = "groups" | "tiers" | "sources" | "lastMet" | "lastEmail" | "lastText" | `has:${string}` | "location" | "company" | "title" | "interest" | "onHub" | "added" | "archived" | "isPrivate" | "memorialized" | `custom:${string}`;

export interface FilterPart {
  key: FilterPartKey;
  clause: string;
}

/** One grammar clause per active row, in the phone's order. Group ids become names when known. */
export function filterParts(f: PeopleFilter, groupNames: Record<string, string> = {}): FilterPart[] {
  const parts: FilterPart[] = [];
  const any = (key: FilterPartKey, clauses: string[]) => {
    if (clauses.length === 1) parts.push({ key, clause: clauses[0] });
    else if (clauses.length > 1) parts.push({ key, clause: `[${clauses.join(" ")}]` });
  };
  any("groups", [...f.groupIds].sort().map((id) => `group:${quote(groupNames[id] ?? id)}`));
  any("tiers", [...f.tiers].sort((a, b) => a - b).filter((t) => TIER_NAMES[t]).map((t) => `relationship:${TIER_NAMES[t]}`));
  any("sources", [...f.sources].sort().map((s) => `source:${s}`));
  const when = (key: FilterPartKey, dateKey: string, countKey: string, w: When) => {
    if (w === "any") return;
    if (w === "never") parts.push({ key, clause: `${countKey}=0` });
    else if (w === "thismonth") parts.push({ key, clause: `${dateKey}:thismonth` });
    else parts.push({ key, clause: `${dateKey}<${w}` });
  };
  when("lastMet", "lastmet", "meetingcount", f.lastMet);
  when("lastEmail", "lastemail", "emailcount", f.lastEmail);
  when("lastText", "lasttext", "messagecount", f.lastText);
  for (const [target] of HAS_TARGETS) {
    const tri = f.has[target] ?? "any";
    if (tri === "has") parts.push({ key: `has:${target}`, clause: `has:${target}` });
    else if (tri === "missing") parts.push({ key: `has:${target}`, clause: `missing:${target}` });
  }
  if (f.location) parts.push({ key: "location", clause: `location:${quote(f.location.toLowerCase())}` });
  if (f.company) parts.push({ key: "company", clause: `company:${quote(f.company.toLowerCase())}` });
  if (f.title) parts.push({ key: "title", clause: `title:${quote(f.title.toLowerCase())}` });
  if (f.interest) parts.push({ key: "interest", clause: `interest:${quote(f.interest.toLowerCase())}` });
  if (f.onHub) parts.push({ key: "onHub", clause: "hub:true" });
  if (f.added !== "any") parts.push({ key: "added", clause: `created>${f.added}` });
  if (f.archived) parts.push({ key: "archived", clause: "is_archived:true" });
  if (f.isPrivate) parts.push({ key: "isPrivate", clause: "is_private:true" });
  if (f.memorialized) parts.push({ key: "memorialized", clause: "is_memorialized:true" });
  for (const k of Object.keys(f.custom).sort()) {
    const v = f.custom[k];
    if (v) parts.push({ key: `custom:${k}`, clause: `${k}:${quote(v.toLowerCase())}` });
  }
  return parts;
}

export function filterGrammar(f: PeopleFilter, groupNames: Record<string, string> = {}): string {
  return filterParts(f, groupNames).map((p) => p.clause).join(" ");
}

export function filterCount(f: PeopleFilter): number {
  return filterParts(f).length;
}

/** The filter with one row back at rest; sort is never touched. */
export function clearPart(f: PeopleFilter, key: FilterPartKey): PeopleFilter {
  const n: PeopleFilter = { ...f, has: { ...f.has }, custom: { ...f.custom } };
  if (key.startsWith("has:")) delete n.has[key.slice(4)];
  else if (key.startsWith("custom:")) delete n.custom[key.slice(7)];
  else {
    switch (key) {
      case "groups": n.groupIds = []; break;
      case "tiers": n.tiers = []; break;
      case "sources": n.sources = []; break;
      case "lastMet": n.lastMet = "any"; break;
      case "lastEmail": n.lastEmail = "any"; break;
      case "lastText": n.lastText = "any"; break;
      case "location": n.location = ""; break;
      case "company": n.company = ""; break;
      case "title": n.title = ""; break;
      case "interest": n.interest = ""; break;
      case "onHub": n.onHub = false; break;
      case "added": n.added = "any"; break;
      case "archived": n.archived = false; break;
      case "isPrivate": n.isPrivate = false; break;
      case "memorialized": n.memorialized = false; break;
    }
  }
  return n;
}

/** Panel grammar and typed query, side by side: the engine reads the space as AND. */
export function composeQuery(grammar: string, typed: string): string {
  return [grammar, typed].map((s) => s.trim()).filter(Boolean).join(" ");
}

const OPERATOR_SYMBOLS: Record<string, string> = { eq: "=", gte: ">=", lte: "<=", gt: ">", lt: "<", contains: ":" };

/** A parsed node back as grammar, so a typed clause can be dropped from the box when its chip is removed. */
export function serializeNode(node: SearchNode): string {
  switch (node.kind) {
    case "all": return "";
    case "text": return quote(node.value);
    case "existence": return `${node.negate ? "missing" : "has"}:${node.target}`;
    case "keyword": return `${node.keyword}${OPERATOR_SYMBOLS[node.operator] ?? ":"}${quote(node.value)}`;
    case "not": return `-${serializeNode(node.child)}`;
    case "or": return `[${node.children.map(serializeNode).filter(Boolean).join(" ")}]`;
    case "and": return `(${node.children.map(serializeNode).filter(Boolean).join(" ")})`;
  }
}

/**
 * The typed query without its nth clause. Only a top-level AND maps chips to clauses one to one; a query that
 * rewrote a natural phrase, or that is a single clause, is cleared whole.
 */
export function removeTypedClause(ast: SearchNode, chipCount: number, index: number): string {
  if (ast.kind !== "and" || ast.children.length !== chipCount) return "";
  return ast.children.filter((_, i) => i !== index).map(serializeNode).filter(Boolean).join(" ");
}
